import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import { breakSelection, getTierList, BreakItem, TierListItem } from "./proApi";
import {
  Champ,
  ChampionDatalist,
  PatchSelect,
  Pct,
  RoleSelect,
  pctColor,
  useProIndex,
} from "./shared";

type Selected = { champ: string; role: string; pct: number | null };

function MetaBreaker() {
  const { champions, patches } = useProIndex();
  const [patch, setPatch] = useState("");
  const [poolRole, setPoolRole] = useState("");
  const [poolQuery, setPoolQuery] = useState("");
  const [weeks, setWeeks] = useState(2);
  const [minRecent, setMinRecent] = useState(0);
  const [pool, setPool] = useState<TierListItem[]>([]);
  const [poolLoading, setPoolLoading] = useState(false);

  const [selected, setSelected] = useState<Selected[]>([]);
  const [mode, setMode] = useState<"opp" | "syn">("opp");
  const [candRole, setCandRole] = useState("");
  const [minCovers, setMinCovers] = useState(1);
  const [results, setResults] = useState<BreakItem[] | null>(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [openRow, setOpenRow] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const minRecentId = useId();
  const weeksId = useId();
  const poolQueryId = useId();
  const minCoversId = useId();
  const usePatch = !!patch;
  const nOf = useCallback(
    (it: TierListItem) => (usePatch ? it.n_patch : it.n_window ?? it.n_recent),
    [usePatch],
  );
  const nLabel = usePatch ? `N ${patch}` : `N ${weeks}w`;

  // ---- champion pool (self tier list) ----
  useEffect(() => {
    let cancelled = false;
    setPoolLoading(true);
    getTierList({
      kind: "self",
      patch: patch || undefined,
      min_recent: 0,
      weeks,
    })
      .then((d) => {
        if (!cancelled) setPool(d.items);
      })
      .catch(() => {})
      .finally(() => !cancelled && setPoolLoading(false));
    return () => {
      cancelled = true;
    };
  }, [patch, weeks]);

  const poolView = useMemo(() => {
    const q = poolQuery.trim().toLowerCase();
    return pool.filter(
      (it) =>
        (!poolRole || it.role === poolRole) &&
        (!q || (it.champ || "").toLowerCase().includes(q)),
    );
  }, [pool, poolRole, poolQuery]);

  const isSelected = useCallback(
    (champ: string, role: string) =>
      selected.some(
        (s) => s.champ.toLowerCase() === champ.toLowerCase() && s.role === role,
      ),
    [selected],
  );

  const toggle = useCallback(
    (champ: string, role: string, pct: number | null = null) => {
      setSelected((prev) =>
        prev.some(
          (s) =>
            s.champ.toLowerCase() === champ.toLowerCase() && s.role === role,
        )
          ? prev.filter(
              (s) =>
                !(
                  s.champ.toLowerCase() === champ.toLowerCase() &&
                  s.role === role
                ),
            )
          : [...prev, { champ, role, pct }],
      );
      setResults(null);
      setOpenRow(null);
    },
    [],
  );

  // ---- run ----
  const run = useCallback(() => {
    if (!selected.length) return;
    setResultsLoading(true);
    setError(null);
    setOpenRow(null);
    breakSelection({
      kind: mode,
      selected: selected.map((s) => [s.champ, s.role]),
      patch: patch || undefined,
      role: candRole || undefined,
    })
      .then((d) => setResults(d.items))
      .catch((e) => setError(e.message || "Error"))
      .finally(() => setResultsLoading(false));
  }, [selected, mode, patch, candRole]);

  const selfIdx = useMemo(() => {
    const idx: Record<string, TierListItem> = {};
    pool.forEach((it) => {
      idx[`${(it.champ || "").toLowerCase()}|${it.role}`] = it;
    });
    return idx;
  }, [pool]);
  const candN = useCallback(
    (champ: string, role: string) => {
      const it = selfIdx[`${champ.toLowerCase()}|${role}`];
      return it ? nOf(it) : 0;
    },
    [selfIdx, nOf],
  );

  // Both candidate filters live here: coverage AND recent activity — the
  // min-games threshold applies to the PROPOSED champions, not the pool.
  const filteredResults = useMemo(
    () =>
      (results || []).filter(
        (it) =>
          it.coverage >= minCovers && candN(it.champ, it.role) >= minRecent,
      ),
    [results, minCovers, candN, minRecent],
  );

  return (
    <div className="pro__page">
      <div className="pro__page-head">
        <h1>Meta breaker</h1>
      </div>
      <p className="pro__page-sub">
        <b>
          Select a combination of champions and see which champions work well
          against them or alongside them.
        </b>
        <br />
        For example, you can select several champions that could play on the
        same team, to see which champions would make good teammates or effective
        opponents.
        <br />
        You can also select several champions in the same role, to find out
        which champion would be effective against all of those options.
      </p>

      <div className="pro__toolbar">
        <PatchSelect patches={patches} value={patch} onChange={setPatch} />
        <ChampionDatalist id="pro-mb-champions" champions={champions} />
      </div>

      <div className="pro__cols">
        <div className="pro__card">
          <div className="pro__card-title">
            Add champion
            <span className="pro__card-note">
              {!poolLoading && `${poolView.length} champions — click to select`}
              {poolLoading && usePatch && `Computing patch ${patch}…`}
              {poolLoading && !usePatch && "Loading…"}
            </span>
          </div>
          <div className="pro-mb__poolfilter">
            <input
              id={poolQueryId}
              type="text"
              list="pro-mb-champions"
              value={poolQuery}
              placeholder="Filter champions…"
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => setPoolQuery(e.target.value)}
            />
            <select
              value={poolRole}
              onChange={(e) => setPoolRole(e.target.value)}
            >
              <option value="">All roles</option>
              {["Top", "Jungle", "Mid", "Bot", "Sup"].map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div className="pro__scroll" style={{ maxHeight: 560 }}>
            {!poolLoading && !poolView.length && (
              <div className="pro__empty">No champion.</div>
            )}
            {poolView.length > 0 && (
              <table className="pro__table">
                <thead>
                  <tr>
                    <th data-tip="Champion and role. Click a row to add it to the selection (click again to remove).">
                      Champion
                    </th>
                    <th data-tip="Solo-strength percentile: how much this champion lifts a draft on its own at this role, among all champion-roles.">
                      Pct
                    </th>
                    <th data-tip="Games played at this role over the selected recent window (competitive tiers 1-3).">
                      {nLabel}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {poolView.map((it) => (
                    <tr
                      key={`${it.champ}|${it.role}`}
                      className={`is-clickable${
                        isSelected(it.champ || "", it.role || "")
                          ? " is-selected"
                          : ""
                      }`}
                      onClick={() =>
                        toggle(it.champ || "", it.role || "", it.pct)
                      }
                    >
                      <td>
                        <Champ name={it.champ || ""} role={it.role} />
                      </td>
                      <td>
                        <Pct value={it.pct} />
                      </td>
                      <td className="pro__muted">{nOf(it)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div>
          <div className="pro__card" style={{ marginBottom: 14 }}>
            <div className="pro__card-title">
              Selection
              <span className="pro__card-note">{selected.length} selected</span>
            </div>
            {!selected.length ? (
              <div className="pro__empty">
                Click champions in the “Add champion” list to select them.
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  padding: "12px 14px",
                }}
              >
                {selected.map((s) => (
                  <span
                    key={`${s.champ}|${s.role}`}
                    className="pro__btn"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: "default",
                    }}
                  >
                    <Champ name={s.champ} role={s.role} />
                    <button
                      type="button"
                      onClick={() => toggle(s.champ, s.role)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#676e80",
                        cursor: "pointer",
                        fontWeight: 800,
                        padding: 0,
                      }}
                      aria-label={`Remove ${s.champ}`}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
              marginBottom: 14,
            }}
          >
            <div className="pro__segmented">
              <button
                type="button"
                className={mode === "opp" ? "is-active" : ""}
                onClick={() => setMode("opp")}
              >
                Against
              </button>
              <button
                type="button"
                className={mode === "syn" ? "is-active" : ""}
                onClick={() => setMode("syn")}
              >
                Same team
              </button>
            </div>
            <RoleSelect
              label="Proposed role"
              value={candRole}
              onChange={setCandRole}
            />
            <label className="pro__field" htmlFor={minCoversId}>
              Min covers ≥
              <input
                id={minCoversId}
                type="number"
                min={1}
                value={minCovers}
                onChange={(e) =>
                  setMinCovers(Math.max(1, parseInt(e.target.value || "1", 10)))
                }
              />
              <span className="pro__muted">/ {selected.length}</span>
            </label>
            <label
              className="pro__field"
              htmlFor={minRecentId}
              data-tip="Proposed champions with fewer recent games than this are hidden from the results."
            >
              Min
              <input
                id={minRecentId}
                type="number"
                min={0}
                value={minRecent}
                onChange={(e) =>
                  setMinRecent(Math.max(0, parseInt(e.target.value || "0", 10)))
                }
              />
              games in the last
              <input
                id={weeksId}
                type="number"
                min={1}
                max={520}
                value={weeks}
                disabled={usePatch}
                data-tip={
                  usePatch
                    ? "With a patch dataset the N columns count games during that patch instead."
                    : "Size of the recent window, in weeks. The N columns count games over this window."
                }
                onChange={(e) =>
                  setWeeks(
                    Math.max(
                      1,
                      Math.min(520, parseInt(e.target.value || "1", 10)),
                    ),
                  )
                }
              />
              weeks
            </label>
            <button
              type="button"
              className="pro__btn pro__btn--primary"
              disabled={!selected.length || resultsLoading}
              onClick={run}
            >
              {resultsLoading ? "Computing…" : "Break this"}
            </button>
          </div>

          {(results || resultsLoading || error) && (
            <div className="pro__card">
              <div className="pro__card-title">
                Champions that best {mode === "syn" ? "pair with" : "counter"}{" "}
                the selection
                <span className="pro__card-note">
                  {results ? `${filteredResults.length} candidates` : ""}
                </span>
              </div>
              <div className="pro__scroll" style={{ maxHeight: 560 }}>
                {error && <div className="pro__empty">{error}</div>}
                {resultsLoading && <div className="pro__empty">Computing…</div>}
                {results && !resultsLoading && !filteredResults.length && (
                  <div className="pro__empty">
                    No champion found — loosen the filters (mode, min covers, or
                    fewer selections).
                  </div>
                )}
                {!resultsLoading && filteredResults.length > 0 && (
                  <table className="pro__table">
                    <thead>
                      <tr>
                        <th data-tip="Average percentile of this candidate's interactions with the selected champions — each pair is rated within its own family (direct matchup, cross-role matchup or synergy), then averaged. Click a row for the per-pair breakdown.">
                          Avg pct
                        </th>
                        <th data-tip="The candidate champion, at its most relevant role.">
                          Champion
                        </th>
                        <th data-tip="How many of the selected champions this candidate has a KNOWN interaction with — known = the pair was seen at least 10 times all-time in competitive play. Pairs below that are too noisy and count neither here nor in the average.">
                          Covers
                        </th>
                        <th data-tip="Solo-strength percentile of the candidate itself at this role — a champion can counter your selection while being weak on its own.">
                          Self pct
                        </th>
                        <th data-tip="Games the candidate played at this role over the selected recent window (competitive tiers 1-3).">
                          {nLabel}
                        </th>
                        <th data-tip="Recency score 0-100, from the number of games over the last 2 weeks (fixed window). Scaled per interaction type: one of the most-played of its type scores ~95, an unplayed one 0.">
                          Recency
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredResults.slice(0, 80).map((it, i) => (
                        <React.Fragment key={`${it.champ}|${it.role}`}>
                          <tr
                            className="is-clickable"
                            onClick={() => setOpenRow(openRow === i ? null : i)}
                          >
                            <td>
                              <Pct value={it.pct} />
                            </td>
                            <td>
                              <Champ name={it.champ} role={it.role} />
                            </td>
                            <td className="pro__muted">
                              {it.coverage}/{selected.length}
                            </td>
                            <td>
                              <Pct value={it.self_pct} />
                            </td>
                            <td className="pro__muted">
                              {usePatch ? it.n_patch : candN(it.champ, it.role)}
                            </td>
                            <td>
                              <Pct value={it.note} />
                            </td>
                          </tr>
                          {openRow === i &&
                            it.details.map((d) => (
                              <tr
                                key={`${d.target}|${d.target_role}|${d.kind}`}
                                className="pro-mb__detail"
                              >
                                <td>
                                  <span
                                    className="pro__pct"
                                    style={{ color: pctColor(d.pct) }}
                                  >
                                    {d.pct == null ? "—" : d.pct}
                                  </span>
                                </td>
                                <td style={{ paddingLeft: 32 }}>
                                  <span className="pro__muted">
                                    {mode === "syn" ? "+" : "vs"}
                                  </span>{" "}
                                  <Champ
                                    name={d.target}
                                    role={d.target_role}
                                    icon={false}
                                  />{" "}
                                  <span
                                    className={`pro__fam-chip pro__fam-chip--${d.kind}`}
                                  >
                                    {d.kind}
                                  </span>
                                </td>
                                <td className="pro__muted">—</td>
                                <td className="pro__muted">—</td>
                                <td className="pro__muted">{d.n}</td>
                                <td className="pro__muted">—</td>
                              </tr>
                            ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MetaBreaker;
